import { ExtractComponentProp } from '@approvalmax/types';
import { Box, toast } from '@approvalmax/ui/src/components';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { constants } from 'modules/common';
import { domain } from 'modules/data';
import { useDispatch, useSelector } from 'modules/react-redux';
import { useEditRequestForm } from 'pages/requestForm/hooks/useEditRequestForm';
import { FC, memo, useCallback } from 'react';
import { commonErrorMessages } from 'services/error/messages';
import { isValidOcrImage } from 'shared/helpers';
import { ContentSplitViewMode, contentSplitViewState, ocrIsLoadingState } from 'shared/states';

import { uploadAttachments } from '../../../actions';
import FileDropProvider from '../../../components/FileDropProvider';
import AddFileButton from '../../../components/filesSection/AddFileButton';
import { ocrAttachmentIdState, useWrappedCheckOcrStatus } from '../../../components/SplitViewWrapper/components';
import { AttachmentKind } from '../../../data/AttachmentKind';
import { getContext } from '../../../selectors/pageSelectors';
import { getRequestTransfer } from '../../../selectors/requestSelectors';
import { customGetRequestTransferState } from '../../../states';
import { messages } from './OcrUploadButton.messages';
import { OcrUploadButtonProps } from './OcrUploadButton.types';

const { commonConstants } = constants;

/**
 * @deprecated Use AttachmentsSection
 */
export const OcrUploadButton: FC<OcrUploadButtonProps> = memo((props) => {
    const { request } = props;

    const context = useSelector(getContext);

    const setContentSplitView = useSetAtom(contentSplitViewState);
    const [ocrAttachmentId, setOcrAttachmentIdState] = useAtom(ocrAttachmentIdState);
    const dispatch = useDispatch();
    const customGetRequestTransfer = useAtomValue(customGetRequestTransferState);

    const { mutateAsync: editRequest } = useEditRequestForm({
        onSuccess: () => {
            checkOcrStatus(4000);
        },
        onError: () => {
            setOcrIsLoading(false);
        },
    });

    const setOcrIsLoading = useSetAtom(ocrIsLoadingState);

    let requestTransfer = useSelector((state) => {
        if (request.integrationType !== domain.IntegrationType.NetSuite) {
            return getRequestTransfer(state, request);
        }

        return null;
    });

    const { checkOcrStatus } = useWrappedCheckOcrStatus(request, ocrAttachmentId || undefined);

    const onUpload = useCallback<ExtractComponentProp<typeof FileDropProvider, 'onUpload'>>(
        async (files, attachmentKind) => {
            let newRequestTransfer = requestTransfer;

            if (customGetRequestTransfer && !requestTransfer) {
                newRequestTransfer = customGetRequestTransfer();
            }

            const processedFiles = await Promise.all(
                files.map(async (file) => {
                    if (file.type.startsWith('image/')) {
                        const url = URL.createObjectURL(file);
                        const isValidImage = await isValidOcrImage(url);

                        if (!isValidImage) {
                            toast.error(commonErrorMessages.ocrImageResolutionError({ fileName: file.name }));

                            return null;
                        }

                        URL.revokeObjectURL(url);
                    }

                    return file;
                })
            );

            const filteredFiles = processedFiles.filter((file) => file !== null);

            dispatch(
                uploadAttachments({
                    files: filteredFiles,
                    attachmentKind,
                    successfulCallback: (attachments) => {
                        setContentSplitView({
                            mode: ContentSplitViewMode.AttachmentsToRequest,
                            attachmentId: attachments[0].id,
                        });
                        setOcrAttachmentIdState(attachments[0].id);
                        setOcrIsLoading(true);

                        editRequest({
                            body: {
                                ...newRequestTransfer,
                                ocrAttachment: attachments[0].id,
                                attachments: newRequestTransfer?.attachments
                                    ? [...newRequestTransfer.attachments, attachments[0].id]
                                    : [attachments[0].id],
                                currency: request.companyCurrency,
                            },
                        });
                    },
                })
            );
        },
        [
            customGetRequestTransfer,
            dispatch,
            editRequest,
            request.companyCurrency,
            requestTransfer,
            setContentSplitView,
            setOcrAttachmentIdState,
            setOcrIsLoading,
        ]
    );

    return (
        <FileDropProvider
            onUpload={onUpload}
            maxSize={context.attachmentMaxSize}
            allowedFileExtensions={commonConstants.ocrAllowedFileExtensionsOldFormat}
            multiple={false}
        >
            <Box spacing='0 60'>
                <AddFileButton title={messages.uploadButtonTitle} attachmentKind={AttachmentKind.General} />
            </Box>
        </FileDropProvider>
    );
});

OcrUploadButton.displayName = 'OcrUploadButton';
